import { FC, useState } from 'react';
import QuestionContentArea from '@/components/ui/QuestionContentArea/QuestionContentArea';
import ReadingInsertButton from '@/components/ui/ReadingInsertButton/ReadingInsertButton';
import ReadingInsertModal from '@/components/ui/ReadingInsertModal/ReadingInsertModal';

interface ParsedQuestionContent {
  hasExtract: boolean;
  questionNumber: string;
  extractInstruction: string;
  extractText: string;
  mainQuestion: string;
  referencedLines: { start: number; end: number } | null;
}

interface AnswerFeedbackProps {
  answerText?: string | null;
  aiFeedback?: string | null;
  aiStrengths?: string[] | null;
  aiImprovements?: string[] | null;
  aiScore?: number | null;
  maxScore?: number;
  gradedAt?: string | null;
  practiceAnswerId?: string;
  existingModelAnswer?: string | null;
  practiceSetId?: string;
  questionContent?: string | null;
  readingInsertContent?: string | null;
  readingInsertPageNumbers?: number[] | null;
  pdfUrl?: string | null;
  imagePageNumbers?: number[] | null;
  imagePdfUrl?: string | null;
}

const parseQuestionContent = (content: string): ParsedQuestionContent => {
  if (!content) {
    return {
      hasExtract: false,
      questionNumber: '',
      extractInstruction: '',
      extractText: '',
      mainQuestion: '',
      referencedLines: null,
    };
  }

  // Check for line references (e.g., "lines 1-3", "from lines 7 to 17", "line 5")
  const lineReferencePatterns = [
    /(?:from\s+)?lines?\s+(\d+)[-–]\s*(\d+)/i, // "lines 1-3" or "from lines 7-17"
    /(?:from\s+)?lines?\s+(\d+)\s+to\s+(\d+)/i, // "lines 1 to 3"
    /line\s+(\d+)/i, // "line 5" (single line)
  ];

  let referencedLines: { start: number; end: number } | null = null;
  for (const pattern of lineReferencePatterns) {
    const match = content.match(pattern);
    if (match) {
      const start = parseInt(match[1], 10);
      const end = match[2] ? parseInt(match[2], 10) : start; // Single line if no end specified
      referencedLines = { start, end };
      break;
    }
  }

  // Check for extract markers added by OpenAI
  const extractStartMarker = '<<<EXTRACT_START>>>';
  const extractEndMarker = '<<<EXTRACT_END>>>';

  if (content.includes(extractStartMarker) && content.includes(extractEndMarker)) {
    const startIndex = content.indexOf(extractStartMarker);
    const endIndex = content.indexOf(extractEndMarker);

    // Extract the parts
    const beforeExtract = content.substring(0, startIndex).trim();
    const extractText = content.substring(startIndex + extractStartMarker.length, endIndex).trim();
    const afterExtract = content.substring(endIndex + extractEndMarker.length).trim();

    // Parse question number and instruction from the first line
    const firstLine = beforeExtract.split('\n')[0] || '';
    const match = firstLine.match(/^(\d+)\s+(.*)/);

    return {
      hasExtract: true,
      questionNumber: match ? match[1] : '',
      extractInstruction: beforeExtract,
      extractText: extractText,
      mainQuestion: afterExtract,
      referencedLines,
    };
  }

  // If no extract markers found, return the whole content as main question
  return {
    hasExtract: false,
    questionNumber: '',
    extractInstruction: '',
    extractText: '',
    mainQuestion: content,
    referencedLines,
  };
};

const AnswerFeedback: FC<AnswerFeedbackProps> = ({
  answerText,
  aiFeedback,
  aiStrengths,
  aiImprovements,
  aiScore,
  maxScore,
  gradedAt,
  questionContent,
  readingInsertContent,
  readingInsertPageNumbers,
  pdfUrl,
  imagePageNumbers,
  imagePdfUrl,
}) => {
  const hasFullMarks = aiScore !== null && maxScore !== undefined && aiScore === maxScore;
  const hasFeedback = aiFeedback || aiStrengths || aiImprovements;
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(true);
  const [showReadingInsert, setShowReadingInsert] = useState(false);

  return (
    <div className="space-y-6">
      {/* Question Section using QuestionContentArea */}
      {questionContent && (
        <QuestionContentArea
          questionContent={questionContent}
          marks={maxScore || 0}
          imagePageNumbers={imagePageNumbers || null}
          pdfUrl={imagePdfUrl || undefined}
          readingInsertContent={readingInsertContent || undefined}
          isCollapsed={isQuestionCollapsed}
          onToggleCollapse={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
          parseQuestionContent={parseQuestionContent}
        />
      )}

      {/* View Full Reading Insert Button (for Past Papers only) */}
      {readingInsertPageNumbers && readingInsertPageNumbers.length > 0 && pdfUrl && (
        <div className="flex justify-center">
          <ReadingInsertButton onClick={() => setShowReadingInsert(true)} variant="primary" />
        </div>
      )}

      {/* Your Answer Section */}
      <div>
        <h3 className="text-lg font-semibold text-white bg-[#2B3440] px-6 py-3 rounded-t-lg">Your Answer</h3>
        <div className="bg-[#2B3440] text-white p-6 rounded-b-lg whitespace-pre-wrap leading-relaxed">
          {answerText || 'No answer provided'}
        </div>
      </div>

      {/* AI Feedback Section */}
      {aiFeedback && (
        <div className="bg-base-100 px-6 py-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Feedback</h3>
          <div
            className="prose max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: aiFeedback }}
          />
        </div>
      )}

      {/* Strengths and Improvements */}
      {(aiStrengths || aiImprovements) && (
        <div className="flex flex-col gap-6 bg-base-100 px-6 py-6 rounded-lg">
          {/* Strengths */}
          {aiStrengths && aiStrengths.length > 0 && (
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Strengths:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {aiStrengths.map((strength: string, idx: number) => (
                    <li key={idx}>{strength}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Improvements - Only show if student didn't get full marks */}
          {!hasFullMarks && aiImprovements && aiImprovements.length > 0 && (
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">To improve:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {aiImprovements.map((improvement: string, idx: number) => (
                    <li key={idx}>{improvement}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show message if no grading yet */}
      {!hasFeedback && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">
            {gradedAt ? 'No AI feedback available for this question' : 'This answer has not been graded yet'}
          </p>
        </div>
      )}

      {/* Reading Insert Modal (for Past Papers) */}
      {readingInsertPageNumbers && readingInsertPageNumbers.length > 0 && pdfUrl && (
        <ReadingInsertModal
          isOpen={showReadingInsert}
          onClose={() => setShowReadingInsert(false)}
          pdfUrl={pdfUrl}
          pageNumbers={readingInsertPageNumbers}
        />
      )}
    </div>
  );
};

export default AnswerFeedback;
